import type { FC } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { ID_TOC_CONTAINER, isAsideArea } from 'src/hooks/page/use-dnd/helper';
import { $dropInfo, getDropInfo } from 'src/services/app/hook/use-drop-info';

let raf: number;
const autoScroll = (x: number, y: number) => {
  const el = document.getElementById(ID_TOC_CONTAINER);
  if (!el) return;
  const rect = el.getBoundingClientRect();

  if (x < rect.left || x > rect.right) return;

  const step = () => {
    if (y - rect.top < 40) {
      const speed = ((y - rect.top - 40) * 10) / 120;
      el.scrollTop += speed;
      raf = requestAnimationFrame(step);
    } else if (rect.height - y < 40) {
      const speed = ((40 - (rect.height - y)) * 10) / 120;
      el.scrollTop += speed;
      raf = requestAnimationFrame(step);
    }
  };
  cancelAnimationFrame(raf);
  raf = requestAnimationFrame(step);
};

export const DragPreview: FC = () => {
  const { cursorArea, dragToc } = $dropInfo((state) => ({
    cursorArea: state?.cursorArea,
    dragToc: state?.tocInfo?.dragToc,
  }));

  const [{ x, y }, setXY] = useState({ x: 0, y: 0 });

  useEffect(() => {
    if (!cursorArea) {
      cancelAnimationFrame(raf);
      return;
    }

    const mousemove = (event: MouseEvent) => {
      const dropInfo = getDropInfo();
      if (dropInfo?.tocInfo?.dragToc) {
        setXY({ x: event.x + 10, y: event.y - 16 });
      }

      if (isAsideArea(cursorArea)) {
        autoScroll(event.x, event.y);
      }
    };

    document.addEventListener('mousemove', mousemove);

    return () => {
      document.removeEventListener('mousemove', mousemove);
    };
  }, [cursorArea]);

  const preview = useMemo(() => {
    const node = document.querySelector(`#${ID_TOC_CONTAINER} [data-block-id="${dragToc}"]`);
    if (!node) return;
    const html = node.outerHTML;
    const rect = node.getBoundingClientRect();
    return {
      html,
      width: rect.width,
      height: rect.height,
    };
  }, [dragToc]);

  if (!cursorArea || !preview) return null;

  return createPortal(
    <div
      className="fixed top-0 left-0 z-[100] rounded opacity-50 pointer-events-none bg-grey6"
      style={{
        width: preview.width,
        height: preview.height,
        transform: `translate3d(${x}px,${y}px, 0)`,
      }}
      dangerouslySetInnerHTML={{ __html: preview.html }}
    />,
    document.body
  );
};
